import React, { useMemo, useState } from 'react';
import { Play, X, ZoomIn } from 'lucide-react';
import { Dialog, DialogContent } from '../ui/dialog';
import { resolveThumbnailUrl } from '../../lib/publicApi';

export default function ProductGallery({ gallery = [] }) {
  const items = useMemo(
    () => [...gallery].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [gallery],
  );
  const [activeIndex, setActiveIndex] = useState(0);
  const [zoomOpen, setZoomOpen] = useState(false);

  if (!items.length) {
    return (
      <div className="aspect-[4/3] rounded-2xl bg-muted flex items-center justify-center text-muted-foreground">
        No preview available
      </div>
    );
  }

  const active = items[activeIndex] || items[0];
  const isVideo = active.type === 'video';
  const activeMediaUrl = resolveThumbnailUrl(active.signed_url);

  const renderMain = () => {
    if (isVideo && activeMediaUrl) {
      return (
        <video
          key={activeMediaUrl || active.image_id || activeIndex}
          src={activeMediaUrl}
          controls
          autoPlay
          className="h-full w-full rounded-2xl bg-black object-contain"
        />
      );
    }
    if (activeMediaUrl) {
      return (
        <button
          type="button"
          className="group relative h-full w-full"
          onClick={() => setZoomOpen(true)}
          aria-label="Zoom image"
        >
          <img
            key={activeMediaUrl}
            src={activeMediaUrl}
            alt=""
            className="h-full w-full rounded-2xl object-contain"
          />
          <span className="absolute bottom-3 right-3 rounded-full bg-black/60 p-2 text-white opacity-0 transition group-hover:opacity-100">
            <ZoomIn className="h-4 w-4" />
          </span>
        </button>
      );
    }
    return <div className="h-full w-full rounded-2xl bg-muted" />;
  };

  return (
    <div className="space-y-4" data-testid="product-gallery">
      <div className="aspect-[4/3] overflow-hidden rounded-2xl border border-border bg-muted/30">
        {renderMain()}
      </div>

      {items.length > 1 && (
        <div className="flex gap-3 overflow-x-auto pb-1">
          {items.map((item, index) => {
            const selected = index === activeIndex;
            return (
              <button
                key={item.image_id || index}
                type="button"
                onClick={() => setActiveIndex(index)}
                className={`relative h-20 w-20 shrink-0 overflow-hidden rounded-xl border-2 transition ${
                  selected ? 'border-primary ring-2 ring-primary/20' : 'border-border hover:border-primary/40'
                }`}
              >
                {item.type === 'video' ? (
                  <div className="flex h-full w-full items-center justify-center bg-slate-900 text-white">
                    <Play className="h-6 w-6" />
                  </div>
                ) : item.signed_url ? (
                  <img
                    key={item.signed_url}
                    src={resolveThumbnailUrl(item.signed_url)}
                    alt=""
                    className="h-full w-full object-contain bg-muted/30"
                  />
                ) : (
                  <div className="h-full w-full bg-muted" />
                )}
              </button>
            );
          })}
        </div>
      )}

      <Dialog open={zoomOpen} onOpenChange={setZoomOpen}>
        <DialogContent className="max-w-5xl border-none bg-black/95 p-2 sm:p-4">
          <button
            type="button"
            className="absolute right-3 top-3 z-10 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
            onClick={() => setZoomOpen(false)}
            aria-label="Close zoom"
          >
            <X className="h-5 w-5" />
          </button>
          {activeMediaUrl && !isVideo && (
            <img src={activeMediaUrl} alt="" className="max-h-[85vh] w-full object-contain" />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
